import { useState, useEffect } from "react";
import HelpButton from "../components/HelpButton";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import WhatsAppSendModal from "../components/WhatsAppSendModal";
import { toast } from "sonner";
import { Plus, WhatsappLogo, Truck } from "@phosphor-icons/react";
import { buildDCVars } from "../utils/whatsappContext";

const DeliveryChallans = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [editor, setEditor] = useState(null);
  const [wa, setWa] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/delivery-challans`);
      setItems(r.data || []);
    } catch { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!editor.customer_name) { toast.error("Customer name required"); return; }
    try {
      await axios.post(`${API}/delivery-challans?requester_role=${user?.role || ""}&requester_user_id=${user?.user_id || ""}&requester_name=${encodeURIComponent(user?.name || "")}`, editor);
      toast.success("DC created"); setEditor(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const openWa = (dc, action) => {
    setWa({
      module: "delivery",
      receiver: { name: dc.customer_name, phone: dc.customer_phone, type: "customer" },
      defaultAction: action,
      context: { variables: buildDCVars(dc), related_doc: { type: "delivery-challan", id: dc.dc_id, number: dc.dc_number } },
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64 p-8">
        <div className="flex justify-between items-start mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-slate-500 mb-1">Logistics</div>
            <div className="flex items-center"><h1 className="text-4xl sm:text-5xl tracking-tighter font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Delivery Challans</h1><HelpButton moduleKey="delivery" /></div>
            <p className="text-base text-slate-700 mt-1">{items.length} DCs · WhatsApp dispatch updates to customers</p>
          </div>
          <button data-testid="dc-new-btn" onClick={() => setEditor({ customer_name: "", customer_phone: "", dispatch_date: "", tracking_no: "", notes: "" })} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2.5 font-medium inline-flex items-center gap-2"><Plus size={16} weight="bold" /> New DC</button>
        </div>

        <div data-testid="dc-table" className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              {["DC #", "Customer", "Dispatch", "Tracking", "Status", "Actions"].map(h => <th key={h} className="text-left p-3 text-xs uppercase tracking-wider font-bold text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.length === 0 ? <tr><td colSpan={6} className="p-12 text-center text-slate-400 text-sm">No delivery challans yet.</td></tr> :
              items.map((dc, i) => (
                <tr key={dc.dc_id} data-testid={`dc-row-${i}`} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="p-3 font-mono font-bold text-[#002FA7]">{dc.dc_number}</td>
                  <td className="p-3 text-sm">{dc.customer_name}</td>
                  <td className="p-3 text-xs text-slate-500">{(dc.dispatch_date || "—").slice(0, 10)}</td>
                  <td className="p-3 font-mono text-xs">{dc.tracking_no || "—"}</td>
                  <td className="p-3"><span className="text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5">{dc.status}</span></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button data-testid={`dc-wa-dispatch-${i}`} onClick={() => openWa(dc, "dispatch_update")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-300 text-emerald-700 hover:bg-emerald-50 font-bold inline-flex items-center gap-1"><WhatsappLogo size={12} weight="fill" />Dispatch</button>
                    <button data-testid={`dc-wa-dc-${i}`} onClick={() => openWa(dc, "send_dc")} className="text-[10px] uppercase tracking-wider px-2 py-1 border border-slate-300 text-slate-700 hover:bg-slate-50 ml-1 font-bold">Send DC</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editor && (
        <div className="fixed inset-0 z-[150] bg-black/50 flex items-center justify-center p-4" data-testid="dc-editor">
          <div className="bg-white border border-slate-200 w-full max-w-xl shadow-2xl">
            <div className="border-b px-5 py-3 flex items-center gap-2"><Truck size={18} className="text-[#002FA7]" /><h3 className="font-semibold">New Delivery Challan</h3></div>
            <div className="p-5 space-y-3">
              {[["customer_name", "Customer Name *"], ["customer_phone", "Customer phone (91XXXXXXXXXX)"], ["customer_address", "Address"], ["dispatch_date", "Dispatch date (YYYY-MM-DD)"], ["tracking_no", "Tracking #"], ["so_number", "SO #"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1 block">{l}</label>
                  <input data-testid={`dc-fld-${k}`} value={editor[k] || ""} onChange={e => setEditor({...editor, [k]: e.target.value})} className="w-full rounded-none border border-slate-300 p-2 text-sm outline-none" />
                </div>
              ))}
            </div>
            <div className="border-t px-5 py-3 flex justify-end gap-2 bg-slate-50">
              <button onClick={() => setEditor(null)} className="rounded-none border border-slate-300 px-4 py-2 text-sm hover:bg-slate-100">Cancel</button>
              <button data-testid="dc-save" onClick={save} className="rounded-none bg-[#002FA7] text-white hover:bg-[#00227A] px-5 py-2 text-sm font-medium">Save</button>
            </div>
          </div>
        </div>
      )}

      {wa && <WhatsAppSendModal open={true} onClose={() => setWa(null)} {...wa} user={user} />}
    </div>
  );
};

export default DeliveryChallans;
